/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../../globalUtils/globalIcons';
import './ClusterBadge.css';
import { Options } from 'highcharts';
import { CustomHighCharts, useAppDispatch } from '../../../globalUtils/globalExports';
import { setChargerIdsForRedirection } from '../../rmsReduxStore/acNocDashboardRedux/acNocDashboardCreator';

const AcClusterBadge: React.FC<{ chargers: IChargerDetails[], showChargingIcon?: boolean | null, clusterId: string, openClusterId: string | null, onToggle: (string) => void }> = ({ chargers, showChargingIcon, clusterId, onToggle, openClusterId }) => {

  const dispatch = useAppDispatch();
  const { chartHeight, totalChargerFontSize } = useMemo(() => {
    const chartHeight = chargers?.length > 999 ? 70 : chargers?.length > 99 ? 60 : chargers?.length > 9 ? 50 : 40;
    const totalChargerFontSize = 12;
    return { chartHeight, totalChargerFontSize };
  }, [chargers]);

  const { totalCount, criticalCount, majorCount, minorCount, activeChargingSessionCount, healthyCount, offlineCount } = useMemo(() => {
    const totalCount = chargers.length;
    const criticalCount = chargers.reduce((sum, c) => sum + (c.alarms.critical ?? 0), 0);
    const majorCount = chargers.reduce((sum, c) => sum + (c.alarms.major ?? 0), 0);
    const minorCount = chargers.reduce((sum, c) => sum + (c.alarms.minor ?? 0), 0);
    const activeChargingSessionCount = chargers.reduce((sum, c) => {
      if (c?.activeChargingSession1 || c?.activeChargingSession2 || c?.activeChargingSession3) {
        return sum + 1;
      } else return sum;
    }, 0);
    const healthyCount = chargers.reduce((sum, c) => {
      const { critical } = c.alarms;
      if (!critical && c.status?.toLowerCase() === 'online') return sum + 1;
      return sum;
    }, 0);
    const offlineCount = chargers.reduce((sum, c) => {
      if (c.status?.toLowerCase() === 'offline' || c.status?.toLowerCase() == null ) return sum + 1;
      return sum;
    }, 0);
    return { totalCount, criticalCount, majorCount, minorCount, activeChargingSessionCount, healthyCount, offlineCount };
  }, [chargers]);

  const getFormattedData = useCallback(() => {
    if (!chargers) return [];
    const filteredData = [
      { name: 'Critical Alarms', value: criticalCount, color: '#AD251B', count: criticalCount },
      { name: 'Major Alarms', value: majorCount, color: '#E17130', count: majorCount },
      { name: 'Minor Alarms', value: minorCount, color: '#F0BB0D', count: minorCount },
      { name: 'Healthy', value: healthyCount, color: '#27C380', count: healthyCount },
      { name: 'Offline', value: offlineCount, color: '#9E9E9E', count: offlineCount },
    ];
    return filteredData;
  }, [chargers]);

  const filteredData = getFormattedData();
  const clusterBadgeChartOptions: Options = useMemo(() => {
    return {
      chart: {
        height: chartHeight,
        width: chartHeight,
        backgroundColor: 'transparent',
        margin: [0, 0, 0, 0],
        events: {
          load: function (): void {
            this.reflow();
          },
          render: function (): void {
            const chart = this as Highcharts.Chart & any;

            chart.customLabel?.destroy?.();

            const text = totalCount;

            const tempLabel = chart.renderer.label(text)
              .css({ fontSize: totalChargerFontSize, textAlign: 'center' })
              .add();

            const bbox = tempLabel.getBBox();

            tempLabel.destroy();

            chart.customLabel = chart.renderer.label(text)
              .css({ fontSize: totalChargerFontSize, textAlign: 'center' })
              .attr({ zIndex: 5 })
              .add();

            chart.customLabel.attr({
              x: chart.plotLeft + chart.plotWidth / 2 - bbox.width / 2,
              y: chart.plotTop + chart.plotHeight / 2 - bbox.height / 2
            });

            chart.customBg?.destroy?.();

            const centerX = chart.plotLeft + chart.plotWidth / 2;
            const centerY = chart.plotTop + chart.plotHeight / 2;
            const innerRadius = chart.series[0].center[3] / 2;

            chart.customBg = chart.renderer
              .circle(centerX, centerY, innerRadius)
              .attr({
                fill: '#fff',
                zIndex: 0,
                cursor: 'pointer'
              })
              .add();

            chart.customDashedCircle?.destroy?.();

            const borderRadius = innerRadius - 3;
            const strokeColor = '#A4A4A4';

            chart.customDashedCircle = chart.renderer
              .circle(centerX, centerY, borderRadius)
              .attr({
                fill: 'none',
                stroke: strokeColor,
                'stroke-width': 0.8,
                dashstyle: 'Dash',
                zIndex: 4
              })
              .add();
          }
        }
      },
      title: {
        text: '',
        align: 'center',
      },
      legend: {
        enabled: false,
      },
      exporting: { enabled: false },
      tooltip: {
        enabled: false,
        shared: true,
        outside: true,
        useHTML: true,
        valueDecimals: 2,
        backgroundColor: 'transparent',
        formatter: function () {
          const tooltipText = `

              `;
          return tooltipText;
        }
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          borderWidth: 0,
          colorByPoint: true,
          type: 'pie',
          size: chartHeight,
          innerSize: chartHeight * 0.8,
          dataLabels: {
            enabled: false
          },
        },
      },
      series: [
        {
          type: 'pie',
          name: '6',
          data: filteredData.map(entry => ({
            name: entry.name,
            y: entry.value,
            color: entry.color,
            count: entry.count
          }))
        },
      ],
    };
  }, [filteredData]);

  const handleNavigation = async (val: string) => {
    let chargerIds: string[] = [];

    switch (val) {
      case 'online':
        chargerIds = chargers?.filter(item => item?.status?.toLowerCase() === 'online')?.map(item => item?.id);
        break;
      case 'offline':
        chargerIds = chargers?.filter(item => item?.status?.toLowerCase() === 'offline' || item?.status?.toLowerCase() == null )?.map(item => item?.id);
        break;
      case 'critical':
        chargerIds = chargers?.filter(item => item?.alarms?.critical === 1)?.map(item => item?.id);
        break;
      case 'major':
        chargerIds = chargers?.filter(item => item?.alarms?.major === 1)?.map(item => item?.id);
        break;
      case 'minor':
        chargerIds = chargers?.filter(item => item?.alarms?.minor === 1)?.map(item => item?.id);
        break;
      case 'healthy':
        chargerIds = chargers?.filter(item => item?.alarms?.critical === 0 && item?.status?.toLowerCase() === 'online')?.map(item => item?.id);
        break;
      default:
        return;
    }

    if (chargerIds?.length === 0) return;
    dispatch(setChargerIdsForRedirection(chargerIds));

    window.open('/ac/manage-chargers?redirectedFrom=nocDashboard', '_blank');
  };

  return (
    <>
      <div className='noc__dashboard__map__cluster__badge__container' onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(clusterId); }}>
        <CustomHighCharts highChartOptions={clusterBadgeChartOptions} />
        {openClusterId === clusterId
          && <div className='noc-dashboard-cluster-badge-tooltip-container' onClick={(e) => e.stopPropagation()}>
            <div className='noc-dashboard-charger-status-container'>
              <div className='noc-dashboard-online-charger-container' onClick={() => { handleNavigation('online'); }}>
                <div className='noc-dashboard-cluster-badge-count'>{totalCount - offlineCount}</div>
                <div className='noc-dashboard-cluster-badge-label'>Online</div>
              </div>
              <div className='noc-dashboard-offline-charger-container' onClick={() => { handleNavigation('offline'); }}>
                <div className='noc-dashboard-cluster-badge-count'>{offlineCount}</div>
                <div className='noc-dashboard-cluster-badge-label'>Offline</div>
              </div>
              <div className='noc-dashboard-healthy-charger-container' onClick={() => { handleNavigation('healthy'); }}>
                <div className='noc-dashboard-cluster-badge-count'>{healthyCount}</div>
                <div className='noc-dashboard-cluster-badge-label'>Healthy</div>
              </div>
            </div>
            <div className='noc-dashboard-charger-alarm-container'>
              <div className='noc-dashboard-charger-alarm-header'>
                <div className='noc-dashboard-charger-alarm-header-label'>Alarm</div>
                <div className='noc-dashboard-charger-alarm-header-count'>{criticalCount + majorCount + minorCount}</div>
              </div>
              <div className='noc-dashboard-charger-alarm-body'>
                <div className='noc-dashboard-critical-charger-container' onClick={() => { handleNavigation('critical'); }}>
                  <div className='noc-dashboard-cluster-badge-count'>{criticalCount}</div>
                  <div className='noc-dashboard-cluster-badge-label'>Critical</div>
                </div>
                <div className='noc-dashboard-major-charger-container' onClick={() => { handleNavigation('major'); }}>
                  <div className='noc-dashboard-cluster-badge-count'>{majorCount}</div>
                  <div className='noc-dashboard-cluster-badge-label'>Major</div>
                </div>
                <div className='noc-dashboard-minor-charger-container' onClick={() => { handleNavigation('minor'); }}>
                  <div className='noc-dashboard-cluster-badge-count'>{minorCount}</div>
                  <div className='noc-dashboard-cluster-badge-label'>Minor</div>
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    </>
  );
};

export default React.memo(AcClusterBadge, (prevProps, nextProps) => {
  if (prevProps.chargers.length !== nextProps.chargers.length) return false;

  const prevIds = prevProps.chargers.map(c => c.id).sort().join(',');
  const nextIds = nextProps.chargers.map(c => c.id).sort().join(',');

  const prevShowChargingIcon = prevProps?.showChargingIcon;
  const nextShowChargingIcon = nextProps?.showChargingIcon;

  return prevIds === nextIds && prevShowChargingIcon === nextShowChargingIcon && prevProps.openClusterId === nextProps.openClusterId;
});
